/**
 * Bundle Comparison — Modal JS
 * File: assets/bundle-comparison.js
 *
 * Handles: dialog open/close (trigger delegation, close button,
 * backdrop click, focus restore), tooltip toggle, image fallback.
 * All comparison rows render at once and scroll vertically inside
 * the modal — there's no expand/collapse step.
 *
 * Sticky header is pure CSS (position: sticky) — no JS needed, since
 * .bc-scroll is the single scroll ancestor for both axes inside the modal.
 */

(function () {
  'use strict';

  var lastTrigger = null;
  var scrollLockY = 0;
  var isScrollLocked = false;

  /* ── Modal open/close ─────────────────────────────────── */
  function getModal() {
    return document.querySelector('.bc-modal');
  }

  /*
   * iOS Safari snaps the underlying page's scroll position to 0 the
   * moment `overflow: hidden` is applied to a scrolled <html>/<body>
   * (the scroll container is destroyed mid-scroll). That's what made
   * the modal appear "at the top of the page" instead of over the
   * trigger — the background page jumped, not the dialog. Pinning
   * body via position:fixed at its current negative offset avoids
   * that scroll-container teardown entirely.
   */
  function lockScroll() {
    if (isScrollLocked) return;
    isScrollLocked = true;
    scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollLockY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.documentElement.style.overflow = 'hidden';
  }

  /* Guarded so a redundant call (e.g. the native "close" event firing
     after closeModal() already unlocked eagerly) can't re-scroll the
     page out from under a user who has since scrolled elsewhere. */
  function unlockScroll() {
    if (!isScrollLocked) return;
    isScrollLocked = false;
    document.documentElement.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    window.scrollTo(0, scrollLockY);
  }

  function openModal(triggerEl) {
    var dialog = getModal();
    if (!dialog || dialog.open) return;
    lastTrigger = triggerEl || document.activeElement;
    /* Capture + lock scroll BEFORE showModal() — showModal() itself can
       reset window.scrollY to 0 (observed in-browser), so reading it
       after opening the dialog would lock in the wrong position. */
    lockScroll();
    dialog.showModal();
    var closeBtn = dialog.querySelector('.bc-modal-close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    var dialog = getModal();
    if (!dialog || !dialog.open) return;
    dialog.close();
    handleModalClosed();
  }

  /*
   * Runs the open -> closed cleanup exactly once per close. Called
   * eagerly from closeModal() for the two paths we control (close
   * button, backdrop click) instead of only from the dialog's native
   * "close" event, since that event's dispatch timing shouldn't be
   * the sole thing standing between a user and their scroll position.
   * Also wired to "close" directly below to cover Esc, which doesn't
   * go through closeModal().
   */
  function handleModalClosed() {
    unlockScroll();
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
    lastTrigger = null;
  }

  /*
   * Two ways to trigger the modal:
   *  1. [data-bc-open] — the dedicated trigger snippet.
   *  2. Any <a> whose href ends in #compare-bundles — lets a merchant
   *     turn ANY existing button (a section's schema-driven CTA link,
   *     a menu item, etc.) into a trigger from the Theme Editor,
   *     no code edit required.
   */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-bc-open], a[href$="#compare-bundles"]');
    if (opener) {
      e.preventDefault();
      openModal(opener);
      return;
    }
    var closer = e.target.closest('[data-bc-close]');
    if (closer) {
      e.preventDefault();
      closeModal();
    }
  });

  function initModalDialog(dialog) {
    /* Close on backdrop click (native <dialog> only auto-closes on Esc) */
    dialog.addEventListener('click', function (e) {
      var rect = dialog.getBoundingClientRect();
      var inBounds = e.clientX >= rect.left && e.clientX <= rect.right &&
                     e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!inBounds) closeModal();
    });

    /* Covers Esc — the one native close path that bypasses closeModal().
       handleModalClosed() is safe to run twice (idempotent), so this
       doesn't conflict with the eager call in closeModal() above. */
    dialog.addEventListener('close', handleModalClosed);
  }

  /* ── Modal init + tooltip + image fallback (per section instance) ──
     `root` is either a <dialog class="bc-modal"> or, when the section's
     "Display as full page" setting is on, a plain <div class="bc-page">
     rendered inline on a shareable page — only the former needs the
     dialog-specific backdrop/Esc close wiring. ── */
  function initSection(root) {
    var shopifyEl = root.closest('[id^="shopify-section-"]');
    if (!shopifyEl) return;

    var tipBtn   = shopifyEl.querySelector('.bc-tip-btn');
    var tipPanel = shopifyEl.querySelector('.bc-tip-panel');

    if (root.tagName === 'DIALOG') initModalDialog(root);

    /* ── Tooltip toggle ───────────────────────────────────── */
    if (tipBtn && tipPanel) {
      tipBtn.addEventListener('click', function () {
        var open = tipBtn.getAttribute('aria-expanded') === 'true';
        tipBtn.setAttribute('aria-expanded', String(!open));
        open ? tipPanel.setAttribute('hidden', '') : tipPanel.removeAttribute('hidden');
      });

      document.addEventListener('click', function (e) {
        if (!tipBtn.contains(e.target) && !tipPanel.contains(e.target)) {
          tipBtn.setAttribute('aria-expanded', 'false');
          tipPanel.setAttribute('hidden', '');
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && tipBtn.getAttribute('aria-expanded') === 'true') {
          tipBtn.setAttribute('aria-expanded', 'false');
          tipPanel.setAttribute('hidden', '');
          tipBtn.focus();
        }
      });
    }

    /* ── Image fallback ───────────────────────────────────── */
    shopifyEl.querySelectorAll('.bc-img').forEach(function (img) {
      img.addEventListener('error', function () {
        var wrap = img.closest('.bc-img-wrap');
        if (!wrap || wrap.querySelector('.bc-img-placeholder')) return;
        img.style.display = 'none';
        var ph = document.createElement('div');
        ph.className = 'bc-img-placeholder';
        ph.setAttribute('aria-hidden', 'true');
        ph.textContent = 'Image';
        wrap.appendChild(ph);
      });
    });
  }

  /* ── Boot — init every modal AND standalone-page instance ──────── */
  function boot() {
    document.querySelectorAll('.bc-modal, .bc-page').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify theme editor live-reload support */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('.bc-modal, .bc-page');
    if (root) initSection(root);
  });

})();
